import type { Customer, CustomerFormData } from '../types/customer';
import api from './api';
import { cache } from '../utils/cache';
import { normalizeListResponse } from '../utils/listResponse';

interface BackendCustomer {
  id: string;
  company_id?: string;
  name: string;
  code?: string;
  email?: string | null;
  phone?: string | null;
  billing_address?: string | null;
  shipping_address?: string | null;
  message_on_invoice?: string | null;
  internal_notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

const transformResponse = (data: BackendCustomer): Customer => {
  return {
    id: data.id,
    name: data.name,
    code: data.code || '',
    email: data.email || '',
    phone: data.phone || '',
    billingAddress: data.billing_address || '',
    shippingAddress: data.shipping_address || '',
    messageOnInvoice: data.message_on_invoice || '',
    internalNotes: data.internal_notes || '',
    createdAt: data.created_at || '',
    updatedAt: data.updated_at || ''
  };
};

export interface ListCustomersParams {
  page?: number;
  size?: number;
  search?: string;
}

export const customerService = {
  getAll: async (opts?: { includeOnboardingSamples?: boolean }): Promise<Customer[]> => {
    const includeSamples = opts?.includeOnboardingSamples === true;
    const fetcher = async () => {
      const params = includeSamples ? { include_onboarding_samples: 'true' } : undefined;
      const response = await api.get<BackendCustomer[]>('/customers', { params });
      return (response.data || []).map(transformResponse);
    };
    // Onboarding sample data is volatile (seeded/cleaned mid-tour); never serve the
    // sample-inclusive list from the 5-min cache, or a fetch that ran before the
    // seed would strand the pickers with a stale sample-less list.
    if (includeSamples) return fetcher();
    return cache.fetch('customers_all', fetcher);
  },

  list: async (params: ListCustomersParams = {}): Promise<{ data: Customer[]; total: number }> => {
    const { page = 1, size = 25, search } = params;
    const query = new URLSearchParams();
    query.set('limit', String(size));
    query.set('offset', String((page - 1) * size));
    query.set('include_meta', 'true');
    if (search && search.trim()) query.set('search', search.trim());
    const response = await api.get(`/customers?${query.toString()}`);
    const normalized = normalizeListResponse<BackendCustomer>(response.data);
    return { data: normalized.data.map(transformResponse), total: normalized.total };
  },

  getById: async (id: string): Promise<Customer | undefined> => {
    return cache.fetch(`customers_${id}`, async () => {
      try {
        const response = await api.get<BackendCustomer>(`/customers/${id}`);
        return transformResponse(response.data);
      } catch {
        return undefined;
      }
    });
  },

  create: async (data: CustomerFormData): Promise<Customer> => {
    const payload = {
      name: data.name,
      email: data.email,
      phone: data.phone || '',
      billing_address: data.billingAddress,
      shipping_address: data.shippingAddress || '',
      message_on_invoice: data.messageOnInvoice || '',
      internal_notes: data.internalNotes || ''
    };
    const response = await api.post<BackendCustomer>('/customers', payload);
    cache.delete('customers_all');
    cache.invalidate('reports_');
    return transformResponse(response.data);
  },

  update: async (id: string, data: CustomerFormData): Promise<Customer> => {
    const payload = {
      name: data.name,
      email: data.email,
      phone: data.phone || '',
      billing_address: data.billingAddress,
      shipping_address: data.shippingAddress || '',
      message_on_invoice: data.messageOnInvoice || '',
      internal_notes: data.internalNotes || ''
    };
    const response = await api.put<BackendCustomer>(`/customers/${id}`, payload);
    cache.delete(`customers_${id}`);
    cache.delete('customers_all');
    cache.invalidate('reports_');
    return transformResponse(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/customers/${id}`);
    cache.delete(`customers_${id}`);
    cache.delete('customers_all');
    cache.invalidate('reports_');
  }
};
